// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * The one-time-delivery state machine — the security-critical core of Burnwell,
 * kept pure and fully unit-tested. It guarantees that a sender releases the
 * secret to exactly ONE recipient (the first to verify and request it) and then
 * burns it, and that a receiver can reveal it at most once.
 *
 * Nothing here touches the network, the DOM or crypto — it is just the rules,
 * so the guarantee is provable by test rather than asserted.
 */
import type { SenderState, ReceiverState } from './types.ts';

/** What the sender should do in response to a receiver's request. */
export interface SenderRequestOutcome {
  /** 'deliver' — encrypt + send the secret; 'gone' — tell them it's already taken. */
  action: 'deliver' | 'gone';
  next: SenderState;
}

/**
 * A receiver requested the secret. Deliver only when armed (nothing delivered
 * yet); any request after that is answered with `gone` and does NOT re-deliver.
 * This is the single-use guarantee: a leaked link cannot pull the secret twice.
 */
export function senderOnRequest(state: SenderState): SenderRequestOutcome {
  if (state === 'armed') return { action: 'deliver', next: 'delivering' };
  return { action: 'gone', next: state };
}

/** The receiver acknowledged receipt — burn the secret. Only valid mid-delivery. */
export function senderOnAck(state: SenderState): SenderState {
  return state === 'delivering' ? 'burned' : state;
}

/**
 * The sender aborted before handing anything over. Allowed only before delivery
 * has begun — once the secret is in flight, it can only complete or fail.
 */
export function senderOnCancel(state: SenderState): SenderState {
  return state === 'composing' || state === 'armed' ? 'cancelled' : state;
}

/** True once the sender is in a terminal state and the room can be torn down. */
export function senderIsDone(state: SenderState): boolean {
  return state === 'burned' || state === 'cancelled';
}

/** A peer (the sender) connected. Move a waiting receiver to ready-to-verify. */
export function receiverOnPeer(state: ReceiverState): ReceiverState {
  return state === 'connecting' ? 'ready' : state;
}

/**
 * The receiver pressed Reveal. Send the request only from `ready` — guarding
 * against a double-tap firing two requests (the second would race the burn).
 */
export function receiverOnReveal(state: ReceiverState): { request: boolean; next: ReceiverState } {
  if (state === 'ready') return { request: true, next: 'requesting' };
  return { request: false, next: state };
}

/** The secret frame arrived. Reveal it exactly once (only while requesting). */
export function receiverOnSecret(state: ReceiverState): { show: boolean; next: ReceiverState } {
  if (state === 'requesting') return { show: true, next: 'revealed' };
  return { show: false, next: state };
}

/** The sender reported the secret was already delivered to someone else. */
export function receiverOnGone(state: ReceiverState): ReceiverState {
  if (state === 'revealed' || state === 'burned') return state;
  return 'gone';
}

/** The receiver cleared the revealed secret from their own screen. */
export function receiverOnBurn(state: ReceiverState): ReceiverState {
  return state === 'revealed' ? 'burned' : state;
}

/** True once the receiver flow can do nothing further. */
export function receiverIsDone(state: ReceiverState): boolean {
  return state === 'burned' || state === 'gone';
}
